import React, { useState, useEffect, useRef } from 'react';
import Prospectos from './Prospectos';
import SeguimientoCrmHome from './components/SeguimientoCrmHome';
import SimuladorComparador from './components/SimuladorComparador';
import SimuladorTasasCatalogo from './components/SimuladorTasasCatalogo';
import ActualizacionesBancoHome from './components/ActualizacionesBancoHome';
import Impresion from './components/Impresion';
import Infonavit from './components/Infonavit';
import MiUsuario from './components/MiUsuario';
import BrokerAsignado from './components/BrokerAsignado';
import ReportesUso from './components/ReportesUso';
import HipotecariasMarca from './components/HipotecariasMarca';
import ReportesAcceso from './components/ReportesAcceso';
import Estadisticas from './components/Estadisticas';
import Prospectacion from './components/Prospectacion';
import CambioPasswordObligatorio from './components/CambioPasswordObligatorio';
import Login from './Login';
import InactivityLogout from './InactivityLogout';
import { getUsuarioActual, limpiarSesion } from './apiAuth';
import { CLAVE_MENU_SEGURO } from './ErrorBoundary';
import { aplicaUltimoModulo, cuentaComoUltimo, menuInicial, guardarUltimoModulo } from './utils/ultimoModulo';
// Roles que ven el botón "Instrucciones para aplicación en tu celular" en
// Broker Asignado. Lista permitida explícita: un rol nuevo no lo ve por defecto.
const ROLES_GUIA_CELULAR = ['usuario', 'administrador_plus', 'semiusuario']; // semiusuario = "Básico" en pantalla
const registrarLogout = async () => {
  try { await fetch('/api/auth/logout', { method: 'POST' }); } catch { /* la sesión se cierra igual del lado del cliente */ }
};
const API = '/api';
const NOMBRE_ROL = { administrador: 'administrador', administrador_plus: 'administrador plus', usuario: 'usuario', inmobiliario: 'inmobiliario', semiusuario: 'básico' };
// Filtro por rol del listado de Usuarios (botones que se encienden y apagan).
const ROLES_FILTRO_U = ['administrador', 'usuario', 'administrador_plus', 'inmobiliario', 'semiusuario'];
const ETIQUETA_ROL_FILTRO = { administrador: 'Administrador', usuario: 'Usuario', administrador_plus: 'Administrador Plus', inmobiliario: 'Inmobiliario', semiusuario: 'Básico', controlador: 'Controlador' };

// Alta/edición de Administrador Plus: qué usuarios (Rol Usuario) puede ver.
function SelectorUsuariosVisibles({ usuarios, seleccion, onChange, st }) {
  const [busqueda, setBusqueda] = useState('');
  const q = busqueda.trim().toLowerCase();
  const visibles = usuarios.filter(u => !q || `${u.usuario} ${u.nombre_completo || ''}`.toLowerCase().includes(q));
  const toggle = (id) => onChange(seleccion.includes(id) ? seleccion.filter(x => x !== id) : [...seleccion, id]);
  return (
    <div style={{ marginBottom: 16, border: '1px solid #fcd34d', backgroundColor: '#fffbeb', borderRadius: 8, padding: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
        <label style={{ ...st.label, marginBottom: 0 }}>
          Usuarios que puede ver <span style={{ fontWeight: 'normal', color: '#6b7280' }}>({seleccion.length} de {usuarios.length} seleccionados · solo lectura)</span>
        </label>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={() => onChange(usuarios.map(u => u.id))} style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: 13 }}>Seleccionar todos</button>
          <button type="button" onClick={() => onChange([])} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 13 }}>Ninguno</button>
        </div>
      </div>
      <input placeholder="Buscar usuario..." value={busqueda} onChange={e => setBusqueda(e.target.value)} style={{ ...st.input, width: '50%', marginBottom: 8 }} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 4, maxHeight: 220, overflowY: 'auto', backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: 6, padding: 8 }}>
        {visibles.map(u => (
          <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 6px', fontSize: 13, cursor: 'pointer', borderRadius: 4, backgroundColor: seleccion.includes(u.id) ? '#dbeafe' : 'transparent' }}>
            <input type="checkbox" checked={seleccion.includes(u.id)} onChange={() => toggle(u.id)} />
            <span><strong>{u.usuario}</strong>{u.nombre_completo ? ` — ${u.nombre_completo}` : ''}{!u.activo ? ' (inactivo)' : ''}</span>
          </label>
        ))}
        {!visibles.length && <span style={{ fontSize: 13, color: '#9ca3af' }}>Sin resultados</span>}
      </div>
    </div>
  );
}

function App() {
  // Sin setter: el cambio de sesión (login o logout) siempre recarga la
  // página (ver Login.jsx y los handlers de logout más abajo) para que
  // ningún estado de la sesión anterior (menu, formularios abiertos, listas
  // cargadas, etc.) sobreviva a un cambio de usuario en el mismo navegador.
  const [usuarioActual] = useState(() => getUsuarioActual());
  const [menu, setMenu] = useState('Usuarios');
  // Mide la altura de la barra superior (header + tabs) y la publica como
  // variable CSS --app-header-h, para que los encabezados fijos de cada módulo
  // se peguen justo debajo de ella y no queden tapados.
  const appHeaderRef = useRef(null);
  useEffect(() => {
    const el = appHeaderRef.current;
    if (!el) return;
    const setVar = () => document.documentElement.style.setProperty('--app-header-h', `${el.offsetHeight}px`);
    setVar();
    const ro = new ResizeObserver(setVar);
    ro.observe(el);
    window.addEventListener('resize', setVar);
    return () => { ro.disconnect(); window.removeEventListener('resize', setVar); };
  }, [usuarioActual, menu]);
  const [usuarios, setUsuarios] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [hipotecariasMarca, setHipotecariasMarca] = useState([]);
  const [catalogo, setCatalogo] = useState([]);
  const [banco, setBanco] = useState('');
  const [cat, setCat] = useState(null);
  const [showFormU, setShowFormU] = useState(false);
  // Filtros del listado de Usuarios (solo en pantalla; se conservan al editar/recargar el listado).
  // rolesOcultosU vacío = todos los roles; un rol que no esté en la lista fija también aparece como botón.
  const [filtroEstadoU, setFiltroEstadoU] = useState('activos');
  const [rolesOcultosU, setRolesOcultosU] = useState([]);
  const rolesFiltroU = [...ROLES_FILTRO_U, ...[...new Set(usuarios.map(u => u.rol))].filter(r => !ROLES_FILTRO_U.includes(r))];
  const usuariosFiltrados = usuarios.filter(u => (filtroEstadoU === 'todos' || (filtroEstadoU === 'activos' ? u.activo : !u.activo))
    && !rolesOcultosU.includes(u.rol));
  const filtrosUCambiados = filtroEstadoU !== 'activos' || rolesOcultosU.length > 0;
  const [showFormB, setShowFormB] = useState(false);
  const [showFormC, setShowFormC] = useState(false);
  const [formU, setFormU] = useState({usuario:'',password:'',nombre_completo:'',email:'',rol:'usuario',broker_id:'',vigencia:'',activo:true,limite_folios_banco_mes:'',limite_folios_infonavit_mes:'',usuarios_visibles:[]});
  const [formB, setFormB] = useState({nombre:'',rfc:'',telefono:'',email:'',entidad_federativa:'',ciudad:'',afirme_ejecutivo:'',afirme_broker:'',banorte_master:'',santander_master:'',scotiabank_master:'',banamex_master:'',banamex_oficina:'',banamex_rfc:'',hsbc_marca:'',hsbc_ejec_apellido_paterno:'',hsbc_ejec_apellido_materno:'',hsbc_ejec_nombres:'',hsbc_ejec_subdireccion:'',hsbc_ejec_no_promotor:'',hsbc_ejec_nombre_sucursal:'',hsbc_ejec_correo:'',banregio_no_nombre_sucursal:'',hipotecaria_marca_id:''});
  const [estadosSEPOMEX, setEstadosSEPOMEX] = useState([]);
  const [ciudadesSEPOMEX, setCiudadesSEPOMEX] = useState([]);
  const [formC, setFormC] = useState({nombre:''});
  const [editU, setEditU] = useState(null);
  const [editB, setEditB] = useState(null);
  const [editC, setEditC] = useState(null);
  const [sepomexFile, setSepomexFile] = useState(null);
  const [sepomexStats, setSepomexStats] = useState(null);
  const [sepomexLoading, setSepomexLoading] = useState(false);
  const [sepomexMessage, setSepomexMessage] = useState('');
  const [formColonia, setFormColonia] = useState({cp:'',colonia:'',municipio:'',ciudad:'',estado:''});
  const [formEntidad, setFormEntidad] = useState({entidad_federativa:'',abreviacion:''});
  const [formConversion, setFormConversion] = useState({banco:'',catalogo_origen:'',id_origen:'',campo_destino:'tipo_credito',tipo_credito:'',esquema_credito:''});
  const [cpBuscando, setCpBuscando] = useState(false);
  
  const menus = ['Usuarios','Brokers','Hipotecarias','Catálogos','SEPOMEX','Reportes de Uso','Reportes de Acceso','Estadísticas','Prospectos','Infonavit','Impresión','Tasas y Condiciones'];
  // Nombre que se muestra en la pestaña cuando difiere de la clave interna del
  // menú. La clave 'Prospectos' se conserva tal cual (estado, permisos, rutas).
  const etiquetasMenu = { Prospectos: 'Captura de Prospectos' };
  const menusAdminOnly = ['Usuarios','Brokers','Hipotecarias','Catálogos','SEPOMEX','Reportes de Uso','Reportes de Acceso','Estadísticas','Tasas y Condiciones'];
  // Administrador Plus: los mismos módulos que un Usuario + Reportes de Uso/Acceso
  // (limitados en backend a los usuarios que tiene asignados).
  const menusAdminPlus = ['Reportes de Uso', 'Reportes de Acceso'];
  const esAdminPlus = usuarioActual?.rol === 'administrador_plus';
  // Inmobiliario: solo su Usuario (cambiar contraseña) + Simuladores y
  // Actualizaciones Bancos, que entran abajo por sus flags acceso_*.
  const esInmobiliario = usuarioActual?.rol === 'inmobiliario';
  // Semiusuario (en pantalla "Básico"): lo mismo que un Usuario menos CRM Hipotecas
  // (el bloqueo real está en backend: limitarSemiusuario en auth/middleware.js).
  // Infonavit sí lo ve, con 0 capturas (botón deshabilitado en Infonavit.jsx).
  const esSemiusuario = usuarioActual?.rol === 'semiusuario';
  // Primer acceso del Básico (mig 116): sin pestañas, solo la pantalla de cambio de contraseña.
  const debeCambiar = usuarioActual?.debe_cambiar_password === true;
  const menusBloqueadosSemiusuario = ['CRM Hipotecas'];
  const menusVisibles = usuarioActual && usuarioActual.rol === 'administrador'
    ? menus
    : esInmobiliario
      ? ['Usuario']
      : ['Usuario', 'Broker Asignado', ...menus.filter(m => !menusAdminOnly.includes(m) || (esAdminPlus && menusAdminPlus.includes(m)))];
  // Prospectación (flag acceso_prospectacion): justo antes de Captura de
  // Prospectos, es decir, después de Broker Asignado.
  const iProspectos = menusVisibles.indexOf('Prospectos');
  const menusConProspectacion = usuarioActual?.acceso_prospectacion === true && iProspectos >= 0
    ? [...menusVisibles.slice(0, iProspectos), 'Prospectación', ...menusVisibles.slice(iProspectos)]
    : menusVisibles;
  const menusConCrm = usuarioActual?.acceso_beta_seguimiento ? [...menusConProspectacion, 'CRM Hipotecas'] : menusConProspectacion;
  const menusConSimulador = usuarioActual?.acceso_simulador_tasas ? [...menusConCrm, 'Simuladores'] : menusConCrm;
  // Actualizaciones Bancos: admin ve todo (subir/dar de baja); acceso_actualizaciones_bancos
  // solo da de alta el tab en modo lectura (ver/descargar), gate real está en ActualizacionesBancoHome.
  const menusConActualizaciones = (usuarioActual?.rol === 'administrador' || usuarioActual?.acceso_actualizaciones_bancos)
    ? [...menusConSimulador, 'Actualizaciones Bancos']
    : menusConSimulador;
  // Orden oficial de los módulos (Usuarios/Brokers son los equivalentes del
  // administrador). Los exclusivos de administración no están en la lista y
  // quedan al final en su orden actual (sort estable).
  const ordenModulos = ['Usuario', 'Usuarios', 'Broker Asignado', 'Brokers', 'Prospectación', 'Simuladores', 'Prospectos', 'Infonavit', 'Impresión', 'CRM Hipotecas', 'Actualizaciones Bancos'];
  const posicionModulo = m => (ordenModulos.includes(m) ? ordenModulos.indexOf(m) : ordenModulos.length);
  const menusFinal = menusConActualizaciones
    .filter(m => !(esSemiusuario && menusBloqueadosSemiusuario.includes(m)))
    .sort((a, b) => posicionModulo(a) - posicionModulo(b));
  const bancos = ['Comunes','Afirme','Banorte','Banamex','Banregio','HSBC','Santander','Scotiabank','SEPOMEX'];
  const cats = {
    Comunes:[{n:'Sexo / Género',t:'cat_genero'},{n:'Abreviaciones de Entidades Federativas',t:'cat_entidad_abrev'},{n:'Status de Crédito',t:'cat_status_credito'},{n:'Conversión de Créditos',t:'cat_conversion_credito'}],
    Afirme:[{n:'Estado Civil',t:'cat_afirme_estado_civil'},{n:'Escolaridad',t:'cat_afirme_escolaridad'},{n:'Situación Vivienda',t:'cat_afirme_situacion_vivienda'},{n:'Ocupación',t:'cat_afirme_ocupacion'},{n:'Giro Independientes',t:'cat_afirme_giro_independientes'},{n:'Sector Laboral',t:'cat_afirme_sector_laboral'},{n:'Destino Crédito',t:'cat_afirme_destino_credito'},{n:'Plazo Crédito',t:'cat_afirme_plazo_credito'},{n:'Forma Enganche',t:'cat_afirme_forma_enganche'},{n:'Tipo Inmueble',t:'cat_afirme_tipo_inmueble'}],
    Banorte:[{n:'Estado Civil',t:'cat_banorte_estado_civil'},{n:'Régimen Conyugal',t:'cat_banorte_regimen_conyugal'},{n:'Tipo Domicilio',t:'cat_banorte_tipo_domicilio'},{n:'Tipo Contrato',t:'cat_banorte_tipo_contrato'},{n:'Posición',t:'cat_banorte_posicion'},{n:'Esquema',t:'cat_banorte_esquema'},{n:'Tipo Crédito',t:'cat_banorte_tipo_credito'},{n:'Destino Crédito',t:'cat_banorte_destino_credito'},{n:'Plazo Crédito',t:'cat_banorte_plazo_credito'}],
    Banamex:[{n:'Estado Civil',t:'cat_banamex_estado_civil'},{n:'Escolaridad',t:'cat_banamex_escolaridad'},{n:'Tipo Contrato',t:'cat_banamex_tipo_contrato'},{n:'Tipo Persona',t:'cat_banamex_tipo_persona'},{n:'Fuente Ingresos',t:'cat_banamex_fuente_ingresos'},{n:'Destino',t:'cat_banamex_destino'},{n:'Producto',t:'cat_banamex_producto'},{n:'Tipo Producto',t:'cat_banamex_tipo_producto'},{n:'Destino Vivienda',t:'cat_banamex_destino_vivienda'},{n:'Plazo Crédito',t:'cat_banamex_plazo_credito'},{n:'Origen Enganche',t:'cat_banamex_origen_enganche'},{n:'Parentesco Coacreditado',t:'cat_banamex_parentesco_coacreditado'}],
    Banregio:[{n:'Estado Civil',t:'cat_banregio_estado_civil'},{n:'Escolaridad',t:'cat_banregio_escolaridad'},{n:'Habita en Casa',t:'cat_banregio_habita_casa'},{n:'Actividad Económica',t:'cat_banregio_actividad_economica'},{n:'Giro',t:'cat_banregio_giro'},{n:'Tipo Contrato',t:'cat_banregio_tipo_contrato'},{n:'Destino Crédito',t:'cat_banregio_destino_credito'},{n:'Esquema Adquisición',t:'cat_banregio_esquema_adquisicion'},{n:'Tipo Inmueble',t:'cat_banregio_tipo_inmueble'},{n:'Plazo Crédito',t:'cat_banregio_plazo_credito'}],
    HSBC:[{n:'Uso Inmueble',t:'cat_hsbc_uso_inmueble'},{n:'Nacionalidad',t:'cat_hsbc_nacionalidad'},{n:'Estado Civil',t:'cat_hsbc_estado_civil'},{n:'Régimen Matrimonial',t:'cat_hsbc_regimen_matrimonial'},{n:'Escolaridad',t:'cat_hsbc_escolaridad'},{n:'Tipo Contrato',t:'cat_hsbc_tipo_contrato'},{n:'Puesto/Ocupación',t:'cat_hsbc_puesto_ocupacion'},{n:'Actividad Giro',t:'cat_hsbc_actividad_giro'},{n:'Plazo Crédito',t:'cat_hsbc_plazo_credito'},{n:'Destino',t:'cat_hsbc_destino'},{n:'Subproducto',t:'cat_hsbc_subproducto'}],
    Santander:[{n:'Nacionalidad',t:'cat_santander_nacionalidad'},{n:'Escolaridad',t:'cat_santander_escolaridad'},{n:'Tipo Identificación',t:'cat_santander_tipo_identificacion'},{n:'Estado Civil',t:'cat_santander_estado_civil'},{n:'Régimen Matrimonial',t:'cat_santander_regimen_matrimonial'},{n:'Vive en Casa',t:'cat_santander_vive_casa'},{n:'Puesto',t:'cat_santander_puesto'},{n:'Ocupación',t:'cat_santander_ocupacion'},{n:'Sector Público',t:'cat_santander_sector_publico'},{n:'Tipo Contrato',t:'cat_santander_tipo_contrato'},{n:'Giro Empresa',t:'cat_santander_giro_empresa'},{n:'Destino',t:'cat_santander_destino'},{n:'Producto',t:'cat_santander_producto'},{n:'Esquema Pagos',t:'cat_santander_esquema_pagos'},{n:'Plazo Crédito',t:'cat_santander_plazo_credito'},{n:'Programa Financiamiento',t:'cat_santander_programa_financiamiento'},{n:'Tipo Cofinanciamiento',t:'cat_santander_tipo_cofinanciamiento'},{n:'Tipo Mejora',t:'cat_santander_tipo_mejora'},{n:'Tipo Liquidez',t:'cat_santander_tipo_liquidez'},{n:'Parentesco Funcionario',t:'cat_santander_parentesco_funcionario'},{n:'Actividad Seguros',t:'cat_santander_actividad_seguros'}],
    Scotiabank:[{n:'Nacionalidad',t:'cat_scotiabank_nacionalidad'},{n:'Escolaridad',t:'cat_scotiabank_escolaridad'},{n:'Estado Civil',t:'cat_scotiabank_estado_civil'},{n:'Régimen Matrimonial',t:'cat_scotiabank_regimen_matrimonial'},{n:'Tipo Vivienda',t:'cat_scotiabank_tipo_vivienda'},{n:'Grupo Vulnerabilidad',t:'cat_scotiabank_grupo_vulnerabilidad'},{n:'Parentesco Coacreditado',t:'cat_scotiabank_parentesco_coacreditado'},{n:'Sector Laboral',t:'cat_scotiabank_sector_laboral'},{n:'Ocupación',t:'cat_scotiabank_ocupacion'},{n:'Actividad Tributaria',t:'cat_scotiabank_actividad_tributaria'},{n:'Tipo Contrato',t:'cat_scotiabank_tipo_contrato'},{n:'Producto/Destino',t:'cat_scotiabank_producto_destino'},{n:'Esquema Financiamiento',t:'cat_scotiabank_esquema_financiamiento'},{n:'Plazo Crédito',t:'cat_scotiabank_plazo_credito'},{n:'Tipo Propiedad',t:'cat_scotiabank_tipo_propiedad'},{n:'Uso Vivienda',t:'cat_scotiabank_uso_vivienda'},{n:'Origen Enganche',t:'cat_scotiabank_origen_enganche'}],
    SEPOMEX:[{n:'Colonias Personalizadas',t:'sepomex_colonias'}]
  };

  useEffect(() => {
    if (!usuarioActual) return;
    if (menu === 'Usuarios') loadU();
    if (menu === 'Brokers') { loadB(); loadHipotecariasMarca(); }
    if (menu === 'SEPOMEX') loadSepomexStats();
  }, [menu, usuarioActual]);

  useEffect(() => {
    // Fix: el catálogo de brokers antes solo se cargaba visitando la pestaña "Brokers",
    // lo que dejaba vacío el selector de broker en el formulario de Usuarios.
    if (usuarioActual && usuarioActual.rol === 'administrador') loadB();
  }, [usuarioActual]);

  // Último módulo (mig 122): rol usuario y Básico entran (login o F5) al último módulo guardado en su
  // cuenta; sin módulo guardado o si ya no lo tienen, al Simulador. Tras "Recargar" en la pantalla de
  // error, una vez a Usuario. Los demás roles, como siempre.
  const ultimoAplica = aplicaUltimoModulo(usuarioActual?.rol);
  const [menuSeguro] = useState(() => {
    try { const m = sessionStorage.getItem(CLAVE_MENU_SEGURO) === '1'; sessionStorage.removeItem(CLAVE_MENU_SEGURO); return m; } catch { return false; }
  });
  useEffect(() => {
    if (usuarioActual && ultimoAplica) {
      setMenu(menuInicial({ rol: usuarioActual.rol, ultimo: usuarioActual.ultimo_modulo, menus: menusFinal, menuSeguro,
        respaldo: esSemiusuario && menusFinal.includes('Prospectación') ? 'Prospectación' : 'Prospectos' }));
    } else if (usuarioActual && !menusFinal.includes(menu)) {
      setMenu(esInmobiliario ? 'Simuladores' : 'Prospectos');
    }
  }, [usuarioActual]);

  useEffect(() => {
    if (!usuarioActual) return;
    const intervalo = setInterval(() => {
      fetch('/api/auth/actividad', { method: 'PUT' }).catch(() => {});
    }, 2 * 60 * 1000); // latido: mantiene viva la sesión única mientras el navegador esté abierto (se libera a los 15 min sin latido)
    return () => clearInterval(intervalo);
  }, [usuarioActual]);

  useEffect(() => {
    if (!usuarioActual || esInmobiliario) return;
    fetch('/api/sepomex/estados').then(r => r.json()).then(data => setEstadosSEPOMEX(data));
  }, [usuarioActual]);

  useEffect(() => {
   if (formB.entidad_federativa) {
    fetch(`/api/sepomex/municipios/${formB.entidad_federativa}`).then(r => r.json()).then(data => setCiudadesSEPOMEX(data));
  } else {
    setCiudadesSEPOMEX([]);
  }
  }, [formB.entidad_federativa]);

    const loadU = async () => {
    const r = await fetch(`${API}/usuarios`);
    const d = await r.json();
    if (Array.isArray(d)) setUsuarios(d);
  };

  const saveU = async () => {
    if (!formU.usuario || (!editU && !formU.password)) {
      alert('Complete campos requeridos');
      return;
    }
    // Desactivar (casilla Activo): el backend le cierra la sesión y ya no puede entrar.
    if (editU && editU.activo && formU.activo === false
      && !window.confirm(`¿Desactivar a ${editU.usuario}? Ya no podrá entrar al sistema`)) return;
    const method = editU ? 'PUT' : 'POST';
    const url = editU ? `${API}/usuarios/${editU.id}` : `${API}/usuarios`;
    const r = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formU)
    });
    if (!r.ok) {
      alert((await r.json().catch(() => ({}))).error || 'No se pudo guardar el usuario.');
      return;
    }
    setShowFormU(false);
    loadU();
  };

  const delU = async (id) => {
    if (!window.confirm('¿Eliminar usuario?')) return;
    const r = await fetch(`${API}/usuarios/${id}`, { method: 'DELETE' });
    if (!r.ok) alert((await r.json().catch(() => ({}))).error || 'No se pudo eliminar el usuario.');
    loadU();
  };

  // Sesión única: libera la sesión de un usuario que quedó bloqueado (p. ej.
  // una sesión colgada en otro equipo). Su sesión anterior se cierra.
  const liberarSesionU = async (u) => {
    if (!window.confirm(`¿Liberar la sesión de ${u.usuario}? Su sesión actual se cerrará y podrá entrar desde otro equipo.`)) return;
    const r = await fetch(`${API}/usuarios/${u.id}/liberar-sesion`, { method: 'POST' });
    if (!r.ok) alert((await r.json().catch(() => ({}))).error || 'No se pudo liberar la sesión.');
    loadU();
  };

  // Bloqueo de login por intentos fallidos: borra sus intentos y deja registro
  // de quién lo desbloqueó (backend: POST /api/usuarios/:id/desbloquear).
  const desbloquearU = async (u) => {
    if (!window.confirm(`¿Desbloquear a ${u.usuario}? Podrá volver a intentar entrar de inmediato.`)) return;
    const r = await fetch(`${API}/usuarios/${u.id}/desbloquear`, { method: 'POST' });
    if (!r.ok) alert((await r.json().catch(() => ({}))).error || 'No se pudo desbloquear al usuario.');
    loadU();
  };

  const loadB = async () => {
    try {
      const r = await fetch(`${API}/brokers`);
      const d = await r.json();
      if (Array.isArray(d)) setBrokers(d);
    } catch (err) {
      console.error('Error cargando brokers:', err);
    }
  };

  const loadHipotecariasMarca = async () => {
    try {
      const r = await fetch(`${API}/hipotecarias-marca`);
      const d = await r.json();
      if (Array.isArray(d)) setHipotecariasMarca(d);
    } catch (err) {
      console.error('Error cargando hipotecarias/marca:', err);
    }
  };

  const saveB = async () => {
 console.log('Guardando broker:', formB);  // ← AGREGAR ESTA LÍNEA 
  if (!formB.nombre) {
      alert('Complete campos requeridos');
      return;
    }
    const method = editB ? 'PUT' : 'POST';
    const url = editB ? `${API}/brokers/${editB.id}` : `${API}/brokers`;
    await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formB)
    });
    setShowFormB(false);
    loadB();
  };

  const delB = async (id) => {
    if (!window.confirm('¿Eliminar broker?')) return;
    await fetch(`${API}/brokers/${id}`, { method: 'DELETE' });
    loadB();
  };

  const loadCat = async (tabla) => {
    if (tabla === 'sepomex_colonias') {
      const r = await fetch(`${API}/sepomex/colonias`);
      const d = await r.json();
      setCatalogo(d);
      return;
    }
    if (tabla === 'cat_entidad_abrev') {
      const r = await fetch(`${API}/catalogos/entidades-abrev`);
      const d = await r.json();
      setCatalogo(d);
      return;
    }
    if (tabla === 'cat_conversion_credito') {
      const r = await fetch(`${API}/catalogos/conversion-credito`);
      const d = await r.json();
      setCatalogo(d);
      return;
    }
    const r = await fetch(`${API}/catalogos/${tabla}`);
    const d = await r.json();
    setCatalogo(d);
  };

  const saveC = async () => {
    if (cat.t === 'sepomex_colonias') {
      if (!formColonia.cp || !formColonia.colonia || !formColonia.estado) {
        alert('CP, Colonia y Estado son requeridos');
        return;
      }
      const method = editC ? 'PUT' : 'POST';
      const url = editC ? `${API}/sepomex/colonias/${editC.id}` : `${API}/sepomex/colonias`;
      await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          codigo_postal: formColonia.cp,
          colonia: formColonia.colonia,
          municipio: formColonia.municipio,
          ciudad: formColonia.ciudad,
          estado: formColonia.estado
        })
      });
      setShowFormC(false);
      setEditC(null);
      loadCat(cat.t);
      return;
    }
    if (cat.t === 'cat_entidad_abrev') {
      if (!formEntidad.entidad_federativa || !formEntidad.abreviacion) {
        alert('Entidad Federativa y Abreviación son requeridos');
        return;
      }
      const method = editC ? 'PUT' : 'POST';
      const url = editC ? `${API}/catalogos/entidades-abrev/${editC.id}` : `${API}/catalogos/entidades-abrev`;
      await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formEntidad)
      });
      setShowFormC(false);
      setEditC(null);
      loadCat(cat.t);
      return;
    }
    if (cat.t === 'cat_conversion_credito') {
      if (!formConversion.banco || !formConversion.catalogo_origen || !formConversion.id_origen || !formConversion.campo_destino) {
        alert('Banco, Catálogo Origen, ID Origen y Campo Destino son requeridos');
        return;
      }
      const method = editC ? 'PUT' : 'POST';
      const url = editC ? `${API}/catalogos/conversion-credito/${editC.id}` : `${API}/catalogos/conversion-credito`;
      await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formConversion)
      });
      setShowFormC(false);
      setEditC(null);
      loadCat(cat.t);
      return;
    }
    if (!formC.nombre) {
      alert('Complete el nombre');
      return;
    }
    const method = editC ? 'PUT' : 'POST';
    const url = editC ? `${API}/catalogos/${cat.t}/${editC.id}` : `${API}/catalogos/${cat.t}`;
    await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formC)
    });
    setShowFormC(false);
    loadCat(cat.t);
  };

  const delC = async (id) => {
    if (!window.confirm('¿Eliminar registro?')) return;
    if (cat.t === 'sepomex_colonias') {
      await fetch(`${API}/sepomex/colonias/${id}`, { method: 'DELETE' });
    } else if (cat.t === 'cat_entidad_abrev') {
      await fetch(`${API}/catalogos/entidades-abrev/${id}`, { method: 'DELETE' });
    } else if (cat.t === 'cat_conversion_credito') {
      await fetch(`${API}/catalogos/conversion-credito/${id}`, { method: 'DELETE' });
    } else {
      await fetch(`${API}/catalogos/${cat.t}/${id}`, { method: 'DELETE' });
    }
    loadCat(cat.t);
  };

  const loadSepomexStats = async () => {
    try {
      const r = await fetch(`${API}/sepomex/stats`);
      const d = await r.json();
      setSepomexStats(d);
    } catch (err) {
      console.error('Error cargando estadísticas SEPOMEX:', err);
    }
  };

  const handleSepomexUpload = async () => {
    if (!sepomexFile) {
      alert('Selecciona un archivo Excel');
      return;
    }
    
    if (!window.confirm('⚠️ ADVERTENCIA: Se eliminarán TODOS los datos SEPOMEX existentes. ¿Continuar?')) {
      return;
    }
    
    setSepomexLoading(true);
    setSepomexMessage('');
    
    const formData = new FormData();
    formData.append('file', sepomexFile);
    
    try {
      const r = await fetch(`${API}/sepomex/import`, {
        method: 'POST',
        body: formData
      });
      
      const d = await r.json();
      
      if (d.success) {
        setSepomexMessage(`✓ ${d.message}`);
        setSepomexFile(null);
        document.getElementById('sepomex-file-input').value = '';
        loadSepomexStats();
      } else {
        setSepomexMessage(`✗ Error: ${d.error}`);
      }
    } catch (err) {
      setSepomexMessage(`✗ Error: ${err.message}`);
    } finally {
      setSepomexLoading(false);
    }
  };
  const st = {
    header: { backgroundColor: '#1a3a6e', padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 },
    logo: { backgroundColor: '#ffffff', width: 42, height: 42, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
    title: { color: 'white', fontSize: 'clamp(15px, 4.2vw, 20px)', fontWeight: 'bold', margin: 0, marginLeft: 10, lineHeight: 1.2 },
    tabs: { backgroundColor: '#d1d5db', padding: '12px 24px 0', display: 'flex', gap: 4 },
    tab: { padding: '12px 24px', border: 'none', borderRadius: '12px 12px 0 0', cursor: 'pointer' },
    content: { backgroundColor: 'white', minHeight: 400, padding: 24 },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { backgroundColor: '#1a3a6e', color: 'white', padding: 12, textAlign: 'left' },
    td: { padding: 12, borderBottom: '1px solid #e5e7eb' },
    btn: { padding: '8px 16px', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 'bold' },
    btnG: { backgroundColor: '#22c55e', color: 'white' },
    btnB: { backgroundColor: '#1a3a6e', color: 'white' },
    input: { width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 4 },
    form: { backgroundColor: '#f9fafb', padding: 16, borderRadius: 8, marginBottom: 24, border: '1px solid #e5e7eb' },
    grid4: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 12 },
    grid3: { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 12 },
    grid2: { display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 12, marginBottom: 12 },
    label: { fontSize: 12, color: '#374151', display: 'block', marginBottom: 4, fontWeight: '500' },
    catBox: { backgroundColor: '#f9fafb', padding: 16, borderRadius: 8, border: '2px solid #e5e7eb', cursor: 'pointer', textAlign: 'center' },
    bankTab: { padding: '10px 16px', border: '2px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontWeight: '600', marginRight: 4 }
  };

  if (!usuarioActual) {
    return <Login />;
  }

  return (
    <div style={{ fontFamily: 'Tahoma', minHeight: '100vh', backgroundColor: '#f3f4f6' }}>
      <InactivityLogout onLogout={async () => { await registrarLogout(); limpiarSesion(); window.location.reload(); }} />
      <div ref={appHeaderRef} style={{ position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={st.header}>
          <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
            <div style={st.logo}>
              <img src="/logo-sih.svg" alt="" style={{ height: 36, width: 36, display: 'block' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <h1 style={st.title}>Sistema Integral de Hipotecas - México</h1>
              <span style={{ color: 'white', fontSize: 11, marginLeft: 10, marginTop: 2 }}>by Rueli TI</span>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <span style={{ color: '#d1d5db' }}>
              👤 {usuarioActual.nombre_completo || usuarioActual.usuario} ({NOMBRE_ROL[usuarioActual.rol] || usuarioActual.rol})
              <button onClick={async () => { await registrarLogout(); limpiarSesion(); window.location.reload(); }} style={{ marginLeft: 12, background: 'none', border: '1px solid #d1d5db', color: '#d1d5db', borderRadius: 4, padding: '4px 10px', cursor: 'pointer' }}>
                Salir
              </button>
            </span>
          </div>
        </div>
        <div style={st.tabs}>
          {(debeCambiar ? [] : menusFinal).map(m => {
            const esReportesAcceso = m === 'Reportes de Acceso';
            const esTituloDosRenglones = m === 'Reportes de Uso' || m === 'Reportes de Acceso' || m === 'Tasas y Condiciones' || m === 'Actualizaciones Bancos' || m === 'Prospectos';
            return (
              <button
                key={m}
                onClick={() => {
                  if (ultimoAplica && m !== menu && cuentaComoUltimo(usuarioActual.rol, m)) guardarUltimoModulo(m);
                  setMenu(m);
                }}
                style={{
                  ...st.tab,
                  backgroundColor: menu === m ? 'white' : (esReportesAcceso ? '#374151' : '#e5e7eb'),
                  color: menu === m ? '#1a3a6e' : (esReportesAcceso ? 'white' : '#4b5563'),
                  fontWeight: menu === m ? 'bold' : '600',
                  ...(esTituloDosRenglones ? { maxWidth: 130, minWidth: 112, padding: '12px 4px', whiteSpace: 'normal', textAlign: 'center', lineHeight: 1.2 } : {}),
                }}
              >
                {esReportesAcceso ? '🕒 ' : ''}{etiquetasMenu[m] || m}
              </button>
            );
          })}
        </div>
      </div>
      <div style={st.content}>
        {debeCambiar ? <CambioPasswordObligatorio /> : <>
        {menu === 'Usuarios' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
              <h2 style={{ fontSize: 24, fontWeight: 'bold' }}>Usuarios del Sistema</h2>
              {!showFormU && (
                <button onClick={() => { setShowFormU(true); setEditU(null); setFormU({ usuario: '', password: '', nombre_completo: '', email: '', rol: 'usuario', broker_id: '', vigencia: '', activo: true, limite_folios_banco_mes: '', limite_folios_infonavit_mes: '', usuarios_visibles: [] }); }} style={{ ...st.btn, ...st.btnG }}>
                  + Nuevo Usuario
                </button>
              )}
            </div>
            {showFormU && (
              <div style={st.form}>
                <h3 style={{ marginBottom: 16 }}>{editU ? 'Editar' : 'Nuevo'} Usuario</h3>
                <div style={st.grid4}>
                  <div>
                    <label style={st.label}>Usuario</label>
                    <input maxLength={13} value={formU.usuario} onChange={e => setFormU({ ...formU, usuario: e.target.value })} style={st.input} />
                  </div>
                  <div>
                    <label style={st.label}>Password{editU ? ' (dejar en blanco para no cambiar)' : ''}</label>
                    <input maxLength={20} value={formU.password} onChange={e => setFormU({ ...formU, password: e.target.value })} style={st.input} />
                  </div>
                  <div>
                    <label style={st.label}>Nombre completo</label>
                    <input value={formU.nombre_completo} onChange={e => setFormU({ ...formU, nombre_completo: e.target.value })} style={st.input} />
                  </div>
                  <div>
                    <label style={st.label}>Email</label>
                    <input type="email" value={formU.email} onChange={e => setFormU({ ...formU, email: e.target.value })} style={st.input} />
                  </div>
                </div>
                <div style={st.grid4}>
                  <div>
                    <label style={st.label}>Rol</label>
                    <select value={formU.rol} onChange={e => {
                      // Básico (semiusuario): folios Banco e Infonavit en 0 si el límite está vacío (conserva el que ya tenga).
                      const rol = e.target.value;
                      const vacio = v => v === '' || v == null;
                      setFormU({ ...formU, rol, ...(rol === 'semiusuario' ? {
                        ...(vacio(formU.limite_folios_banco_mes) ? { limite_folios_banco_mes: '0' } : {}),
                        ...(vacio(formU.limite_folios_infonavit_mes) ? { limite_folios_infonavit_mes: '0' } : {}),
                      } : {}) });
                    }} style={st.input} disabled={editU && editU.rol === 'administrador'}>
                      <option value="administrador">Administrador</option>
                      <option value="administrador_plus">Administrador Plus</option>
                      <option value="usuario">Usuario</option>
                      <option value="semiusuario">Básico</option>
                      <option value="inmobiliario">Inmobiliario</option>
                    </select>
                  </div>
                  <div>
                    <label style={st.label}>Vigencia{formU.rol === 'administrador' ? ' (sin límite)' : ''}</label>
                    <input type="date" value={formU.vigencia} disabled={formU.rol === 'administrador'} onChange={e => setFormU({ ...formU, vigencia: e.target.value })} style={st.input} />
                  </div>
                </div>
                {editU && (() => {
                  // Solo en edición. No se puede desactivar al administrador ni a uno mismo.
                  const fijo = editU.rol === 'administrador' || editU.id === usuarioActual?.id;
                  return (
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ ...st.label, display: 'inline-flex', alignItems: 'center', gap: 8, cursor: fijo ? 'not-allowed' : 'pointer' }}>
                        <input type="checkbox" checked={formU.activo !== false} disabled={fijo} onChange={e => setFormU({ ...formU, activo: e.target.checked })} />
                        Activo
                        {fijo && <span style={{ fontWeight: 'normal', fontSize: 12, color: '#6b7280' }}>(no se puede desactivar {editU.rol === 'administrador' ? 'al administrador' : 'tu propio usuario'})</span>}
                      </label>
                    </div>
                  );
                })()}
                {(formU.rol === 'usuario' || formU.rol === 'semiusuario' || formU.rol === 'administrador_plus' || formU.rol === 'inmobiliario') && (
                  <div style={{ marginBottom: 12 }}>
                    <label style={st.label}>Broker{formU.rol === 'inmobiliario' ? ' (logo y color del PDF del comparativo)' : ''}</label>
                    <select value={formU.broker_id} onChange={e => setFormU({ ...formU, broker_id: e.target.value })} style={{ ...st.input, width: '50%' }}>
                      <option value="">--Seleccionar--</option>
                      {brokers.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
                    </select>
                  </div>
                )}
                {(formU.rol === 'usuario' || formU.rol === 'semiusuario' || formU.rol === 'administrador_plus') && (
                  <div style={st.grid4}>
                    <div>
                      <label style={st.label}>Folios Banco aperturados en mes (límite)</label>
                      <input type="number" min="0" placeholder="Sin límite" value={formU.limite_folios_banco_mes} onChange={e => setFormU({ ...formU, limite_folios_banco_mes: e.target.value })} style={st.input} />
                    </div>
                    <div>
                      <label style={st.label}>Folios Infonavit aperturados en mes (límite)</label>
                      <input type="number" min="0" placeholder={formU.rol === 'semiusuario' ? '0' : 'Sin límite'} value={formU.limite_folios_infonavit_mes} onChange={e => setFormU({ ...formU, limite_folios_infonavit_mes: e.target.value })} style={st.input} />
                    </div>
                  </div>
                )}
                {formU.rol === 'administrador_plus' && (
                  <SelectorUsuariosVisibles
                    usuarios={usuarios.filter(u => u.rol === 'usuario')}
                    seleccion={formU.usuarios_visibles || []}
                    onChange={ids => setFormU({ ...formU, usuarios_visibles: ids })}
                    st={st}
                  />
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setShowFormU(false)} style={{ ...st.btn, backgroundColor: '#d1d5db' }}>Cancelar</button>
                  <button onClick={saveU} style={{ ...st.btn, ...st.btnB }}>Guardar</button>
                </div>
              </div>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Estado</span>
              <select aria-label="Estado" value={filtroEstadoU} onChange={e => setFiltroEstadoU(e.target.value)} style={{ ...st.input, width: 'auto', marginBottom: 0 }}>
                <option value="activos">Activos</option>
                <option value="inactivos">Inactivos</option>
                <option value="todos">Todos</option>
              </select>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginLeft: 8 }}>Rol</span>
              {rolesFiltroU.map(r => {
                const on = !rolesOcultosU.includes(r);
                return (
                  <button key={r} type="button" aria-pressed={on} onClick={() => setRolesOcultosU(on ? [...rolesOcultosU, r] : rolesOcultosU.filter(x => x !== r))}
                    style={{ padding: '4px 10px', borderRadius: 999, fontSize: 12, cursor: 'pointer', border: `1px solid ${on ? '#2563eb' : '#d1d5db'}`, backgroundColor: on ? '#dbeafe' : 'white', color: on ? '#1d4ed8' : '#6b7280', fontWeight: on ? 'bold' : 'normal' }}>
                    {ETIQUETA_ROL_FILTRO[r] || r}
                  </button>
                );
              })}
              <span style={{ marginLeft: 'auto', fontSize: 13, color: filtrosUCambiados || usuariosFiltrados.length !== usuarios.length ? '#b45309' : '#374151', fontWeight: 600 }}>
                Mostrando {usuariosFiltrados.length} de {usuarios.length} usuarios
              </span>
              <button type="button" onClick={() => { setFiltroEstadoU('activos'); setRolesOcultosU([]); }} disabled={!filtrosUCambiados}
                style={{ background: 'none', border: 'none', color: filtrosUCambiados ? '#2563eb' : '#9ca3af', cursor: filtrosUCambiados ? 'pointer' : 'default', fontSize: 13, textDecoration: 'underline' }}>
                Limpiar filtros
              </button>
            </div>
            <table style={st.table}>
              <thead>
                <tr>
                  <th style={st.th}>ID</th>
                  <th style={st.th}>Usuario</th>
                  <th style={st.th}>Rol</th>
                  <th style={st.th}>Broker</th>
                  <th style={st.th}>Vigencia</th>
                  <th style={st.th}>Estado</th>
                  <th style={st.th}>Folios Banco (mes)</th>
                  <th style={st.th}>Folios Infonavit (mes)</th>
                  <th style={st.th}>Conectado</th>
                  <th style={{ ...st.th, textAlign: 'center' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {usuariosFiltrados.length === 0 && (
                  <tr><td colSpan={10} style={{ ...st.td, textAlign: 'center', color: '#6b7280', padding: 24 }}>No hay usuarios con estos filtros</td></tr>
                )}
                {usuariosFiltrados.map((u, i) => (
                  <tr key={u.id} style={{ backgroundColor: i % 2 ? 'white' : '#f9fafb' }}>
                    <td style={st.td}>{u.id}</td>
                    <td style={{ ...st.td, fontWeight: 'bold' }}>{u.usuario}</td>
                    <td style={st.td}>
                      <span style={{ padding: '4px 8px', borderRadius: 4, fontSize: 12, backgroundColor: u.rol === 'administrador' ? '#f3e8ff' : u.rol === 'administrador_plus' ? '#fef3c7' : u.rol === 'inmobiliario' ? '#dcfce7' : u.rol === 'semiusuario' ? '#e0f2fe' : '#dbeafe', color: u.rol === 'administrador' ? '#7c3aed' : u.rol === 'administrador_plus' ? '#b45309' : u.rol === 'inmobiliario' ? '#15803d' : u.rol === 'semiusuario' ? '#0369a1' : '#2563eb' }}>
                        {NOMBRE_ROL[u.rol] || u.rol}
                      </span>
                    </td>
                    <td style={st.td}>{u.broker_nombre || '-'}</td>
                    <td style={st.td}>{u.vigencia ? new Date(u.vigencia).toLocaleDateString() : '-'}</td>
                    <td style={st.td}>
                      <span style={{ padding: '4px 8px', borderRadius: 4, fontSize: 12, backgroundColor: u.activo ? '#dcfce7' : '#fee2e2', color: u.activo ? '#16a34a' : '#dc2626' }}>
                        {u.activo ? 'Activo' : 'Inactivo'}
                      </span>
                      {u.bloqueado_hasta && (
                        <div style={{ marginTop: 6 }}>
                          <span title="Bloqueado por intentos fallidos de inicio de sesión" style={{ padding: '4px 8px', borderRadius: 4, fontSize: 12, backgroundColor: '#fee2e2', color: '#b91c1c', whiteSpace: 'nowrap' }}>
                            🔒 Bloqueado hasta {new Date(u.bloqueado_hasta).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: false })}
                          </span>
                        </div>
                      )}
                    </td>
                    <td style={st.td}>{u.rol !== 'administrador' && u.rol !== 'inmobiliario' ? `${u.folios_banco_mes ?? 0}${u.limite_folios_banco_mes != null ? ' / ' + u.limite_folios_banco_mes : ''}` : '-'}</td>
                    <td style={st.td}>{u.rol !== 'administrador' && u.rol !== 'inmobiliario' ? `${u.folios_infonavit_mes ?? 0}${u.limite_folios_infonavit_mes != null ? ' / ' + u.limite_folios_infonavit_mes : ''}` : '-'}</td>
                    <td style={st.td}>
                      {u.sesion_conectado ? (
                        <span title={`Última actividad: ${new Date(u.sesion_ultima_actividad).toLocaleString()}`}>
                          <span style={{ padding: '4px 8px', borderRadius: 4, fontSize: 12, backgroundColor: '#dcfce7', color: '#16a34a' }}>Conectado</span>
                          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                            {u.sesion_navegador} · hace {Math.max(0, Math.round((Date.now() - new Date(u.sesion_ultima_actividad)) / 60000))} min
                          </div>
                        </span>
                      ) : '-'}
                    </td>
                    <td style={{ ...st.td, textAlign: 'center' }}>
                      {u.bloqueado_hasta && (
                        <button onClick={() => desbloquearU(u)} style={{ color: '#b91c1c', background: 'none', border: 'none', cursor: 'pointer', marginRight: 8, fontWeight: 'bold' }}>
                          Desbloquear
                        </button>
                      )}
                      {u.sesion_conectado && u.rol !== 'administrador' && !u.permitir_multiples_sesiones && ( /* marca solo en BD (mig 093) */
                        <button onClick={() => liberarSesionU(u)} style={{ color: '#b45309', background: 'none', border: 'none', cursor: 'pointer', marginRight: 8 }}>
                          Liberar sesión
                        </button>
                      )}
                      <button onClick={() => { setEditU(u); setFormU({ usuario: u.usuario, password: '', nombre_completo: u.nombre_completo || '', email: u.email || '', rol: u.rol, broker_id: u.broker_id || '', vigencia: u.vigencia ? u.vigencia.split('T')[0] : '', activo: u.activo, limite_folios_banco_mes: u.limite_folios_banco_mes ?? '', limite_folios_infonavit_mes: u.limite_folios_infonavit_mes ?? '', usuarios_visibles: u.usuarios_visibles || [] }); setShowFormU(true); }} style={{ color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', marginRight: 8 }}>
                        Editar
                      </button>
                      {u.rol !== 'administrador' && (
                        <button onClick={() => delU(u.id)} style={{ color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }}>
                          Eliminar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {menu === 'Brokers' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
              <h2 style={{ fontSize: 24, fontWeight: 'bold' }}>Catálogo de Brokers</h2>
              {!showFormB && (
                <button onClick={() => { setShowFormB(true); setEditB(null); setFormB({ nombre: '', rfc: '', telefono: '', email: '', entidad_federativa: '', ciudad: '', afirme_ejecutivo: '', afirme_broker: '', banorte_master: '', santander_master: '', scotiabank_master: '', banamex_master: '', banamex_oficina: '', banamex_rfc: '', hsbc_marca: '', hsbc_ejec_apellido_paterno: '', hsbc_ejec_apellido_materno: '', hsbc_ejec_nombres: '', hsbc_ejec_subdireccion: '', hsbc_ejec_no_promotor: '', hsbc_ejec_nombre_sucursal: '', hsbc_ejec_correo: '', banregio_no_nombre_sucursal: '', hipotecaria_marca_id: '' }); }} style={{ ...st.btn, ...st.btnG }}>
                  + Nuevo Broker
                </button>
              )}
            </div>
            {showFormB && (
              <div style={st.form}>
                <h3 style={{ marginBottom: 12 }}>{editB ? 'Editar' : 'Nuevo'} Broker</h3>
                <p style={{ fontSize: 12, fontWeight: 'bold', borderBottom: '1px solid #e5e7eb', paddingBottom: 4, marginBottom: 8 }}>Datos Generales</p>
		<div style={st.grid4}>
  		<div><label style={st.label}>Nombre</label><input value={formB.nombre} onChange={e => setFormB({ ...formB, nombre: e.target.value })} style={st.input} /></div>
  		<div><label style={st.label}>RFC</label><input maxLength={13} value={formB.rfc} onChange={e => setFormB({ ...formB, rfc: e.target.value.toUpperCase() })} style={st.input} /></div>
  		<div><label style={st.label}>Teléfono</label><input maxLength={10} value={formB.telefono} onChange={e => setFormB({ ...formB, telefono: e.target.value.replace(/\D/g, '') })} style={st.input} /></div>
  		<div><label style={st.label}>Email</label><input value={formB.email} onChange={e => setFormB({ ...formB, email: e.target.value })} style={st.input} /></div>
		</div>
		<div style={st.grid2}>
  		<div><label style={st.label}>Entidad Federativa</label><select value={formB.entidad_federativa || ''} onChange={e => setFormB({ ...formB, entidad_federativa: e.target.value, ciudad: '' })} style={st.input}><option value="">-- Seleccionar --</option>{estadosSEPOMEX.map(e => <option key={e.estado} value={e.estado}>{e.estado}</option>)} </select></div>
  		<div><label style={st.label}>Ciudad</label><select value={formB.ciudad || ''} onChange={e => setFormB({ ...formB, ciudad: e.target.value })} style={st.input} disabled={!formB.entidad_federativa}><option value="">-- Seleccionar --</option>{ciudadesSEPOMEX.map(c => <option key={c.municipio} value={c.municipio}>{c.municipio}</option>)} </select></div>
		</div>
                <div style={st.grid2}>
                  <div>
                    <label style={st.label}>Hipotecaria / Marca (logo y color de título en el PDF del Comparativo)</label>
                    <select value={formB.hipotecaria_marca_id || ''} onChange={e => setFormB({ ...formB, hipotecaria_marca_id: e.target.value })} style={st.input}>
                      <option value="">-- Ninguna (usa el look por default) --</option>
                      {hipotecariasMarca.filter(h => h.activo || h.id === Number(formB.hipotecaria_marca_id)).map(h => <option key={h.id} value={h.id}>{h.nombre}</option>)}
                    </select>
                  </div>
                </div>
                <p style={{ fontSize: 12, fontWeight: 'bold', borderBottom: '1px solid #e5e7eb', paddingBottom: 4, marginBottom: 8, color: '#1a3a6e' }}>Afirme</p>
                <div style={st.grid2}>
                  <div><label style={st.label}>Ejecutivo</label><input value={formB.afirme_ejecutivo} onChange={e => setFormB({ ...formB, afirme_ejecutivo: e.target.value })} style={st.input} /></div>
                  <div><label style={st.label}>Broker</label><input value={formB.afirme_broker} onChange={e => setFormB({ ...formB, afirme_broker: e.target.value })} style={st.input} /></div>
                </div>
                <p style={{ fontSize: 12, fontWeight: 'bold', borderBottom: '1px solid #e5e7eb', paddingBottom: 4, marginBottom: 8, color: '#1a3a6e' }}>Banorte</p>
                <div style={{ marginBottom: 12 }}>
                  <label style={st.label}>Master Broker</label>
                  <input value={formB.banorte_master} onChange={e => setFormB({ ...formB, banorte_master: e.target.value })} style={{ ...st.input, width: '50%' }} />
                </div>
                <p style={{ fontSize: 12, fontWeight: 'bold', borderBottom: '1px solid #e5e7eb', paddingBottom: 4, marginBottom: 8, color: '#1a3a6e' }}>Santander</p>
                <div style={{ marginBottom: 12 }}>
                  <label style={st.label}>Master Broker</label>
                  <input value={formB.santander_master || ''} onChange={e => setFormB({ ...formB, santander_master: e.target.value })} style={{ ...st.input, width: '50%' }} />
                </div>
                <p style={{ fontSize: 12, fontWeight: 'bold', borderBottom: '1px solid #e5e7eb', paddingBottom: 4, marginBottom: 8, color: '#1a3a6e' }}>Scotiabank</p>
                <div style={{ marginBottom: 12 }}>
                  <label style={st.label}>Master Broker</label>
                  <input value={formB.scotiabank_master || ''} onChange={e => setFormB({ ...formB, scotiabank_master: e.target.value })} style={{ ...st.input, width: '50%' }} />
                </div>
                <p style={{ fontSize: 12, fontWeight: 'bold', borderBottom: '1px solid #e5e7eb', paddingBottom: 4, marginBottom: 8, color: '#1a3a6e' }}>Banamex</p>
                <div style={st.grid3}>
                  <div><label style={st.label}>Master</label><input value={formB.banamex_master} onChange={e => setFormB({ ...formB, banamex_master: e.target.value })} style={st.input} /></div>
                  <div><label style={st.label}>Oficina</label><input value={formB.banamex_oficina} onChange={e => setFormB({ ...formB, banamex_oficina: e.target.value })} style={st.input} /></div>
                  <div><label style={st.label}>RFC</label><input maxLength={13} value={formB.banamex_rfc} onChange={e => setFormB({ ...formB, banamex_rfc: e.target.value.toUpperCase() })} style={st.input} /></div>
                </div>
                <p style={{ fontSize: 12, fontWeight: 'bold', borderBottom: '1px solid #e5e7eb', paddingBottom: 4, marginBottom: 8, color: '#1a3a6e' }}>HSBC</p>
                <div style={{ marginBottom: 12 }}>
                  <label style={st.label}>Marca (Master Broker)</label>
                  <input value={formB.hsbc_marca} onChange={e => setFormB({ ...formB, hsbc_marca: e.target.value })} style={{ ...st.input, width: '50%' }} />
                </div>
                <p style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 8 }}>Ejecutivo HSBC asignado</p>
                <div style={st.grid3}>
                  <div><label style={st.label}>Apellido Paterno</label><input value={formB.hsbc_ejec_apellido_paterno || ''} onChange={e => setFormB({ ...formB, hsbc_ejec_apellido_paterno: e.target.value })} style={st.input} /></div>
                  <div><label style={st.label}>Apellido Materno</label><input value={formB.hsbc_ejec_apellido_materno || ''} onChange={e => setFormB({ ...formB, hsbc_ejec_apellido_materno: e.target.value })} style={st.input} /></div>
                  <div><label style={st.label}>Nombre(s)</label><input value={formB.hsbc_ejec_nombres || ''} onChange={e => setFormB({ ...formB, hsbc_ejec_nombres: e.target.value })} style={st.input} /></div>
                </div>
                <div style={st.grid4}>
                  <div><label style={st.label}>Subdirección</label><input value={formB.hsbc_ejec_subdireccion || ''} onChange={e => setFormB({ ...formB, hsbc_ejec_subdireccion: e.target.value })} style={st.input} /></div>
                  <div><label style={st.label}>No. de Promotor</label><input value={formB.hsbc_ejec_no_promotor || ''} onChange={e => setFormB({ ...formB, hsbc_ejec_no_promotor: e.target.value })} style={st.input} /></div>
                  <div><label style={st.label}>Nombre Sucursal</label><input value={formB.hsbc_ejec_nombre_sucursal || ''} onChange={e => setFormB({ ...formB, hsbc_ejec_nombre_sucursal: e.target.value })} style={st.input} /></div>
                  <div><label style={st.label}>Correo Ejecutivo</label><input value={formB.hsbc_ejec_correo || ''} onChange={e => setFormB({ ...formB, hsbc_ejec_correo: e.target.value })} style={st.input} /></div>
                </div>
                <p style={{ fontSize: 12, fontWeight: 'bold', borderBottom: '1px solid #e5e7eb', paddingBottom: 4, marginBottom: 8, color: '#1a3a6e' }}>Banregio</p>
                <div style={{ marginBottom: 12 }}>
                  <label style={st.label}>No. y Nombre Sucursal</label>
                  <input value={formB.banregio_no_nombre_sucursal || ''} onChange={e => setFormB({ ...formB, banregio_no_nombre_sucursal: e.target.value })} style={{ ...st.input, width: '50%' }} />
                </div>
                <div style={{ display: 'flex', gap: 8, borderTop: '1px solid #e5e7eb', paddingTop: 12 }}>
                  <button onClick={() => setShowFormB(false)} style={{ ...st.btn, backgroundColor: '#d1d5db' }}>Cancelar</button>
                  <button onClick={saveB} style={{ ...st.btn, ...st.btnB }}>Guardar</button>
                </div>
              </div>
            )}
            {!showFormB && (
              <table style={st.table}>
                <thead>
                  <tr>
                    <th style={st.th}>ID</th>
                    <th style={st.th}>Nombre</th>
                    <th style={st.th}>RFC</th>
                    <th style={st.th}>Teléfono</th>
                    <th style={st.th}>Email</th>
                    <th style={st.th}>Marca</th>
                    <th style={{ ...st.th, textAlign: 'center' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {brokers.map((b, i) => (
                    <tr key={b.id} style={{ backgroundColor: i % 2 ? 'white' : '#f9fafb' }}>
                      <td style={st.td}>{b.id}</td>
                      <td style={{ ...st.td, fontWeight: 'bold' }}>{b.nombre}</td>
                      <td style={st.td}>{b.rfc}</td>
                      <td style={st.td}>{b.telefono}</td>
                      <td style={st.td}>{b.email}</td>
                      <td style={st.td}>{b.hipotecaria_marca_nombre || '—'}</td>
                      <td style={{ ...st.td, textAlign: 'center' }}>
                        <button onClick={() => { setEditB(b); setFormB(b); setShowFormB(true); }} style={{ color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', marginRight: 8 }}>
                          Editar
                        </button>
                        <button onClick={() => delB(b.id)} style={{ color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }}>
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {menu === 'Hipotecarias' && <HipotecariasMarca />}

        {menu === 'Catálogos' && (
          <div>
            <h2 style={{ fontSize: 24, fontWeight: 'bold', marginBottom: 16 }}>Catálogos del Sistema</h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 24 }}>
              {bancos.map(b => (
                <button key={b} onClick={() => { setBanco(b); setCat(null); setCatalogo([]); setShowFormC(false); }} style={{ ...st.bankTab, backgroundColor: banco === b ? '#1a3a6e' : 'white', color: banco === b ? 'white' : '#1f2937', borderColor: banco === b ? '#1a3a6e' : '#d1d5db' }}>
                  {b}
                </button>
              ))}
            </div>
            {banco && !cat && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
                {cats[banco]?.map(c => (
                  <div key={c.t} onClick={() => { setCat(c); loadCat(c.t); setShowFormC(false); setEditC(null); }} style={st.catBox}>
                    <p style={{ fontWeight: '600', color: '#1f2937', margin: 0 }}>{c.n}</p>
                  </div>
                ))}
              </div>
            )}
            {cat && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <button onClick={() => { setCat(null); setCatalogo([]); setShowFormC(false); }} style={{ fontSize: 20, background: 'none', border: 'none', cursor: 'pointer', color: '#4b5563' }}>
                      ←
                    </button>
                    <h3 style={{ fontWeight: 'bold', margin: 0 }}>{cat.n}</h3>
                    <span style={{ backgroundColor: '#f97316', color: 'white', padding: '4px 12px', borderRadius: 12, fontSize: 12 }}>
                      {catalogo.length} registros
                    </span>
                  </div>
                  {!showFormC && (
                    <button onClick={() => { setShowFormC(true); setEditC(null); if (cat.t === 'sepomex_colonias') { setFormColonia({cp:'',colonia:'',municipio:'',ciudad:'',estado:''}); } else if (cat.t === 'cat_entidad_abrev') { setFormEntidad({entidad_federativa:'',abreviacion:''}); } else if (cat.t === 'cat_conversion_credito') { setFormConversion({banco:'',catalogo_origen:'',id_origen:'',campo_destino:'tipo_credito',tipo_credito:'',esquema_credito:''}); } else { setFormC({ nombre: '' }); } }} style={{ ...st.btn, ...st.btnG }}>
                      + Agregar
                    </button>
                  )}
                </div>
                {showFormC && cat.t === 'cat_entidad_abrev' && (
                  <div style={st.form}>
                    <h4 style={{ marginBottom: 12 }}>{editC ? 'Editar' : 'Nueva'} Abreviación</h4>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                      <div style={{ flex: 2 }}>
                        <label style={st.label}>Entidad Federativa</label>
                        <input value={formEntidad.entidad_federativa} onChange={e => setFormEntidad(f => ({...f, entidad_federativa: e.target.value}))} style={st.input} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={st.label}>Abreviación</label>
                        <input value={formEntidad.abreviacion} onChange={e => setFormEntidad(f => ({...f, abreviacion: e.target.value}))} style={st.input} />
                      </div>
                      <button onClick={() => { setShowFormC(false); setEditC(null); }} style={{ ...st.btn, backgroundColor: '#d1d5db', height: 38 }}>
                        Cancelar
                      </button>
                      <button onClick={saveC} style={{ ...st.btn, ...st.btnB, height: 38 }}>
                        Guardar
                      </button>
                    </div>
                  </div>
                )}
                {showFormC && cat.t === 'sepomex_colonias' && (
                  <div style={st.form}>
                    <h4 style={{ marginBottom: 12 }}>{editC ? 'Editar' : 'Nueva'} Colonia</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr 1fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
                      <div>
                        <label style={st.label}>C.P.</label>
                        <input
                          maxLength={5}
                          value={formColonia.cp}
                          onChange={async e => {
                            const cp = e.target.value.replace(/\D/g,'');
                            setFormColonia(f => ({...f, cp}));
                            if (cp.length === 5) {
                              setCpBuscando(true);
                              try {
                                const r = await fetch(`${API}/sepomex/cp/${cp}`);
                                const d = await r.json();
                                if (d.length > 0) {
                                  setFormColonia(f => ({...f, municipio: d[0].municipio||'', ciudad: d[0].ciudad||'', estado: d[0].estado||''}));
                                }
                              } finally { setCpBuscando(false); }
                            }
                          }}
                          style={st.input}
                          placeholder="00000"
                        />
                      </div>
                      <div>
                        <label style={st.label}>Colonia</label>
                        <input value={formColonia.colonia} onChange={e => setFormColonia(f => ({...f, colonia: e.target.value}))} style={st.input} />
                      </div>
                      <div>
                        <label style={st.label}>Municipio {cpBuscando ? '...' : ''}</label>
                        <input value={formColonia.municipio} onChange={e => setFormColonia(f => ({...f, municipio: e.target.value}))} style={st.input} />
                      </div>
                      <div>
                        <label style={st.label}>Ciudad</label>
                        <input value={formColonia.ciudad} onChange={e => setFormColonia(f => ({...f, ciudad: e.target.value}))} style={st.input} />
                      </div>
                      <div>
                        <label style={st.label}>Estado</label>
                        <input value={formColonia.estado} onChange={e => setFormColonia(f => ({...f, estado: e.target.value}))} style={st.input} />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => { setShowFormC(false); setEditC(null); }} style={{ ...st.btn, backgroundColor: '#d1d5db' }}>Cancelar</button>
                      <button onClick={saveC} style={{ ...st.btn, ...st.btnB }}>Guardar</button>
                    </div>
                  </div>
                )}
                {showFormC && cat.t === 'cat_conversion_credito' && (
                  <div style={st.form}>
                    <h4 style={{ marginBottom: 12 }}>{editC ? 'Editar' : 'Nueva'} Conversión</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 100px 1fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
                      <div>
                        <label style={st.label}>Banco</label>
                        <select value={formConversion.banco} onChange={e => setFormConversion(f => ({...f, banco: e.target.value}))} style={st.input}>
                          <option value="">-- Elegir --</option>
                          {['Afirme','Banorte','Banamex','Banregio','HSBC','Santander','Scotiabank'].map(b => (
                            <option key={b} value={b}>{b}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label style={st.label}>Catálogo Origen</label>
                        <input value={formConversion.catalogo_origen} onChange={e => setFormConversion(f => ({...f, catalogo_origen: e.target.value}))} style={st.input} placeholder="cat_hsbc_destino" />
                      </div>
                      <div>
                        <label style={st.label}>ID Origen</label>
                        <input type="number" value={formConversion.id_origen} onChange={e => setFormConversion(f => ({...f, id_origen: e.target.value}))} style={st.input} />
                      </div>
                      <div>
                        <label style={st.label}>Campo Destino</label>
                        <select value={formConversion.campo_destino} onChange={e => setFormConversion(f => ({...f, campo_destino: e.target.value}))} style={st.input}>
                          <option value="tipo_credito">tipo_credito</option>
                          <option value="esquema_credito">esquema_credito</option>
                          <option value="ambos">ambos</option>
                        </select>
                      </div>
                      <div>
                        <label style={st.label}>Tipo de Crédito</label>
                        <input value={formConversion.tipo_credito} onChange={e => setFormConversion(f => ({...f, tipo_credito: e.target.value}))} style={st.input} />
                      </div>
                      <div>
                        <label style={st.label}>Esquema de Crédito</label>
                        <input value={formConversion.esquema_credito} onChange={e => setFormConversion(f => ({...f, esquema_credito: e.target.value}))} style={st.input} />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => { setShowFormC(false); setEditC(null); }} style={{ ...st.btn, backgroundColor: '#d1d5db' }}>Cancelar</button>
                      <button onClick={saveC} style={{ ...st.btn, ...st.btnB }}>Guardar</button>
                    </div>
                  </div>
                )}
                {showFormC && cat.t !== 'sepomex_colonias' && cat.t !== 'cat_entidad_abrev' && cat.t !== 'cat_conversion_credito' && (
                  <div style={st.form}>
                    <h4 style={{ marginBottom: 12 }}>{editC ? 'Editar' : 'Nuevo'} Registro</h4>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                      <div style={{ flex: 1 }}>
                        <label style={st.label}>Nombre</label>
                        <input value={formC.nombre} onChange={e => setFormC({ ...formC, nombre: e.target.value })} style={st.input} />
                      </div>
                      <button onClick={() => { setShowFormC(false); setEditC(null); setFormC({ nombre: '' }); }} style={{ ...st.btn, backgroundColor: '#d1d5db', height: 38 }}>
                        Cancelar
                      </button>
                      <button onClick={saveC} style={{ ...st.btn, ...st.btnB, height: 38 }}>
                        Guardar
                      </button>
                    </div>
                  </div>
                )}
                {cat.t === 'sepomex_colonias' ? (
                  <table style={st.table}>
                    <thead>
                      <tr>
                        <th style={{...st.th, width: 60}}>ID</th>
                        <th style={{...st.th, width: 80}}>C.P.</th>
                        <th style={st.th}>Colonia</th>
                        <th style={st.th}>Municipio</th>
                        <th style={st.th}>Ciudad</th>
                        <th style={st.th}>Estado</th>
                        <th style={{ ...st.th, textAlign: 'center', width: 130 }}>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {catalogo.map((c, i) => (
                        <tr key={c.id} style={{ backgroundColor: i % 2 ? 'white' : '#f9fafb' }}>
                          <td style={st.td}>{c.id}</td>
                          <td style={st.td}>{c.codigo_postal}</td>
                          <td style={st.td}>{c.colonia}</td>
                          <td style={st.td}>{c.municipio}</td>
                          <td style={st.td}>{c.ciudad}</td>
                          <td style={st.td}>{c.estado}</td>
                          <td style={{ ...st.td, textAlign: 'center' }}>
                            <button onClick={() => { setEditC(c); setFormColonia({cp: c.codigo_postal, colonia: c.colonia, municipio: c.municipio||'', ciudad: c.ciudad||'', estado: c.estado}); setShowFormC(true); }} style={{ color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', marginRight: 8 }}>
                              Editar
                            </button>
                            <button onClick={() => delC(c.id)} style={{ color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }}>
                              Eliminar
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : cat.t === 'cat_entidad_abrev' ? (
                  <table style={st.table}>
                    <thead>
                      <tr>
                        <th style={{...st.th, width: 60}}>ID</th>
                        <th style={st.th}>Entidad Federativa</th>
                        <th style={st.th}>Abreviación</th>
                        <th style={{ ...st.th, textAlign: 'center', width: 150 }}>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {catalogo.map((c, i) => (
                        <tr key={c.id} style={{ backgroundColor: i % 2 ? 'white' : '#f9fafb' }}>
                          <td style={st.td}>{c.id}</td>
                          <td style={st.td}>{c.entidad_federativa}</td>
                          <td style={st.td}>{c.abreviacion}</td>
                          <td style={{ ...st.td, textAlign: 'center' }}>
                            <button onClick={() => { setEditC(c); setFormEntidad({ entidad_federativa: c.entidad_federativa, abreviacion: c.abreviacion }); setShowFormC(true); }} style={{ color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', marginRight: 8 }}>
                              Editar
                            </button>
                            <button onClick={() => delC(c.id)} style={{ color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }}>
                              Eliminar
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : cat.t === 'cat_conversion_credito' ? (
                  <table style={st.table}>
                    <thead>
                      <tr>
                        <th style={{...st.th, width: 50}}>ID</th>
                        <th style={st.th}>Banco</th>
                        <th style={st.th}>Catálogo Origen</th>
                        <th style={{...st.th, width: 80}}>ID Origen</th>
                        <th style={st.th}>Campo Destino</th>
                        <th style={st.th}>Tipo de Crédito</th>
                        <th style={st.th}>Esquema de Crédito</th>
                        <th style={{ ...st.th, textAlign: 'center', width: 150 }}>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {catalogo.map((c, i) => (
                        <tr key={c.id} style={{ backgroundColor: i % 2 ? 'white' : '#f9fafb' }}>
                          <td style={st.td}>{c.id}</td>
                          <td style={st.td}>{c.banco}</td>
                          <td style={st.td}>{c.catalogo_origen}</td>
                          <td style={st.td}>{c.id_origen}</td>
                          <td style={st.td}>{c.campo_destino}</td>
                          <td style={st.td}>{c.tipo_credito || ''}</td>
                          <td style={st.td}>{c.esquema_credito || ''}</td>
                          <td style={{ ...st.td, textAlign: 'center' }}>
                            <button onClick={() => { setEditC(c); setFormConversion({ banco: c.banco, catalogo_origen: c.catalogo_origen, id_origen: c.id_origen, campo_destino: c.campo_destino, tipo_credito: c.tipo_credito || '', esquema_credito: c.esquema_credito || '' }); setShowFormC(true); }} style={{ color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', marginRight: 8 }}>
                              Editar
                            </button>
                            <button onClick={() => delC(c.id)} style={{ color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }}>
                              Eliminar
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <table style={st.table}>
                    <thead>
                      <tr>
                        <th style={st.th}>ID</th>
                        <th style={st.th}>Nombre</th>
                        <th style={{ ...st.th, textAlign: 'center', width: 150 }}>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {catalogo.map((c, i) => (
                        <tr key={c.id} style={{ backgroundColor: i % 2 ? 'white' : '#f9fafb' }}>
                          <td style={st.td}>{c.id}</td>
                          <td style={st.td}>{c.nombre}</td>
                          <td style={{ ...st.td, textAlign: 'center' }}>
                            <button onClick={() => { setEditC(c); setFormC({ nombre: c.nombre }); setShowFormC(true); }} style={{ color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', marginRight: 8 }}>
                              Editar
                            </button>
                            <button onClick={() => delC(c.id)} style={{ color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }}>
                              Eliminar
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
            {!banco && <p style={{ color: '#4b5563', textAlign: 'center', padding: 32 }}>Selecciona un banco</p>}
          </div>
        )}

        {menu === 'SEPOMEX' && (
          <div>
            <h2 style={{ fontSize: 24, fontWeight: 'bold', marginBottom: 16 }}>Módulo SEPOMEX</h2>
            <p style={{ color: '#4b5563', marginBottom: 24 }}>Importación de códigos postales de México</p>
            
            {sepomexStats && (
              <div style={{ backgroundColor: '#f0f9ff', border: '1px solid #0ea5e9', borderRadius: 8, padding: 20, marginBottom: 24 }}>
                <h3 style={{ fontSize: 18, fontWeight: 'bold', color: '#0369a1', marginBottom: 12 }}>📊 Estadísticas Actuales</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16 }}>
                  <div>
                    <p style={{ fontSize: 14, color: '#64748b', margin: 0 }}>Total Registros</p>
                    <p style={{ fontSize: 24, fontWeight: 'bold', color: '#1e293b', margin: 0 }}>{Number(sepomexStats.total_registros).toLocaleString()}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: 14, color: '#64748b', margin: 0 }}>Estados</p>
                    <p style={{ fontSize: 24, fontWeight: 'bold', color: '#1e293b', margin: 0 }}>{sepomexStats.total_estados}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: 14, color: '#64748b', margin: 0 }}>Códigos Postales Únicos</p>
                    <p style={{ fontSize: 24, fontWeight: 'bold', color: '#1e293b', margin: 0 }}>{Number(sepomexStats.total_codigos_postales).toLocaleString()}</p>
                  </div>
                </div>
              </div>
            )}
            
            <div style={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: 24 }}>
              <h3 style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 16 }}>📤 Importar Datos SEPOMEX</h3>
              
              <div style={{ backgroundColor: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 8, padding: 16, marginBottom: 20 }}>
                <p style={{ fontSize: 14, color: '#78350f', margin: 0, marginBottom: 8 }}><strong>⚠️ Importante:</strong></p>
                <ul style={{ fontSize: 14, color: '#78350f', margin: 0, paddingLeft: 20 }}>
                  <li>El archivo Excel debe contener las hojas con datos de SEPOMEX</li>
                  <li>Se limpiarán todos los datos existentes antes de importar</li>
                  <li>Campos requeridos: d_codigo, d_mnpio, d_estado</li>
                  <li>El proceso puede tardar varios minutos</li>
                </ul>
              </div>
              
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 8 }}>
                  Seleccionar archivo Excel
                </label>
                <input 
                  id="sepomex-file-input"
                  type="file" 
                  accept=".xlsx,.xls"
                  onChange={(e) => setSepomexFile(e.target.files[0])}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '10px',
                    border: '2px dashed #d1d5db',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontSize: 14
                  }}
                />
                {sepomexFile && (
                  <p style={{ fontSize: 14, color: '#059669', marginTop: 8 }}>
                    ✓ Archivo seleccionado: {sepomexFile.name}
                  </p>
                )}
              </div>
              
              <button 
                onClick={handleSepomexUpload}
                disabled={sepomexLoading || !sepomexFile}
                style={{
                  ...st.btn,
                  ...st.btnB,
                  opacity: sepomexLoading || !sepomexFile ? 0.5 : 1,
                  cursor: sepomexLoading || !sepomexFile ? 'not-allowed' : 'pointer'
                }}
              >
                {sepomexLoading ? '⏳ Importando...' : '🚀 Importar Datos'}
              </button>
              
              {sepomexMessage && (
                <div style={{
                  marginTop: 16,
                  padding: 12,
                  borderRadius: 8,
                  backgroundColor: sepomexMessage.startsWith('✓') ? '#d1fae5' : '#fee2e2',
                  border: `1px solid ${sepomexMessage.startsWith('✓') ? '#10b981' : '#ef4444'}`,
                  color: sepomexMessage.startsWith('✓') ? '#065f46' : '#991b1b'
                }}>
                  {sepomexMessage}
                </div>
              )}
            </div>
          </div>
        )}
{menu === 'Usuario' && <MiUsuario usuarioActual={usuarioActual} />}
{menu === 'Broker Asignado' && <BrokerAsignado mostrarGuiaCelular={ROLES_GUIA_CELULAR.includes(usuarioActual?.rol)} />}
{menu === 'Reportes de Uso' && <ReportesUso />}
{menu === 'Reportes de Acceso' && <ReportesAcceso />}
{menu === 'Estadísticas' && <Estadisticas />}
{menu === 'Prospectación' && <Prospectacion />}
{menu === 'Prospectos' && <Prospectos usuarioActual={usuarioActual} />}
{menu === 'CRM Hipotecas' && <SeguimientoCrmHome />}
{menu === 'Simuladores' && <SimuladorComparador />}
{menu === 'Tasas y Condiciones' && <SimuladorTasasCatalogo />}
{menu === 'Actualizaciones Bancos' && <ActualizacionesBancoHome soloLectura={usuarioActual?.rol !== 'administrador'} />}
{menu === 'Infonavit' && <Infonavit usuarioActual={usuarioActual} />}
{menu === 'Impresión' && <Impresion />}
        </>}
      </div>
    </div>
  );
}
export default App;
